import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { Button, IButtonProps } from '@/common/components/Button/Button';
import { Stack } from '@/common/components/Stack/Stack';
import { useBreakpoints } from '@/common/hooks/useBreakpoints';
import { useFlowLayout } from '@/common/layouts/FlowLayout/FlowLayout.context';

export type TFlowLayoutFooterProps = {
  canGoBack?: boolean;
  primaryAction?: IButtonProps;
  secondaryAction?: IButtonProps;
  tertiaryAction?: IButtonProps;
  onDismiss?: () => void;
};

export function FlowLayoutFooter({
  canGoBack,
  primaryAction,
  secondaryAction,
  tertiaryAction,
  onDismiss,
}: TFlowLayoutFooterProps) {
  const { t } = useTranslation();
  const breakpoints = useBreakpoints(['lg']);
  const { goNext, goBack } = useFlowLayout();

  return (
    <View className={styles.root}>
      <View className={styles.content}>
        <Stack horizontal justify="between" gap="lg">
          {breakpoints.lg && (
            <Button variant="tertiary" onPress={onDismiss}>
              {t('general.navigation.cancel')}
            </Button>
          )}
          <Stack horizontal grow justify="end" className={styles.actions}>
            {tertiaryAction && (
              <Button
                {...tertiaryAction}
                variant="tertiary"
                className={styles.tertiaryAction}
              >
                {tertiaryAction.children}
              </Button>
            )}
            {breakpoints.lg && canGoBack && (
              <Button
                {...secondaryAction}
                variant="secondary"
                onPress={secondaryAction?.onPress ?? goBack}
              >
                {secondaryAction?.children ?? t('general.navigation.back')}
              </Button>
            )}
            <Button
              {...primaryAction}
              onPress={primaryAction?.onPress ?? goNext}
            >
              {primaryAction?.children ?? t('general.navigation.next')}
            </Button>
          </Stack>
        </Stack>
      </View>
    </View>
  );
}

const styles = {
  root: 'py-lg border-t border-neutral20 bg-neutral20',
  content: 'w-full mx-auto max-w-[80rem] px-lg md:px-xl lg:px-2xl',
  actions: 'flex-col lg:flex-row gap-sm lg:gap-lg',
  tertiaryAction: 'order-2 lg:order-none',
};
